package com.narbase.letsgo.web.views.searchIndex

import com.narbase.kunafa.core.components.*
import com.narbase.kunafa.core.components.layout.LinearLayout
import com.narbase.kunafa.core.css.*
import com.narbase.kunafa.core.dimensions.dependent.matchParent
import com.narbase.kunafa.core.dimensions.dependent.wrapContent
import com.narbase.kunafa.core.dimensions.percent
import com.narbase.kunafa.core.dimensions.px
import com.narbase.kunafa.core.drawable.Color
import com.narbase.kunafa.core.lifecycle.LifecycleOwner
import com.narbase.letsgo.web.common.AppColors
import com.narbase.letsgo.web.dto.areas.AreaDto
import com.narbase.letsgo.web.dto.zones.PriceZoneDto
import com.narbase.letsgo.web.network.remoteProcess
import com.narbase.letsgo.web.routing.domain.admin.areas.GetAreasEndpoint
import com.narbase.letsgo.web.routing.domain.admin.zones.GetPriceZonesEndpoint
import com.narbase.letsgo.web.routing.domain.admin.favoritepoints.GetFavoritePointsSearchStatusEndpoint
import com.narbase.letsgo.web.routing.domain.admin.pointofinterest.GetPointsOfInterestSearchStatusEndpoint
import com.narbase.letsgo.web.theme.theme
import com.narbase.letsgo.web.translations.localized
import com.narbase.letsgo.web.utils.horizontalFiller
import com.narbase.letsgo.web.utils.scrollable.scrollable
import com.narbase.letsgo.web.utils.table.headerCell
import com.narbase.letsgo.web.utils.table.listTable
import com.narbase.letsgo.web.utils.table.tableCell
import com.narbase.letsgo.web.utils.table.tableRow
import com.narbase.letsgo.web.utils.verticalFiller
import com.narbase.letsgo.web.utils.verticalSeparator
import com.narbase.letsgo.web.utils.views.*
import com.narbase.letsgo.web.utils.views.customViews.CustomCheckBox
import com.narbase.letsgo.web.utils.views.customViews.customCheckBox

class SearchIndexManagementComponent : Component() {

    private val viewModel = SearchIndexManagementViewModel()

    private var paginationControls: PaginationControls? = null
    private var pointsTableBody: View? = null
    private var settingsContainer: LinearLayout? = null
    private var settingsUrlInput: TextInput? = null
    private var settingsIsActiveCheckBox: CustomCheckBox? = null

    override fun onViewMounted(lifecycleOwner: LifecycleOwner) {
        super.onViewMounted(lifecycleOwner)
        viewModel.getStatus()
        viewModel.getFavoritePointsStatus()
        viewModel.searchPoints()
    }

    override fun View?.getView() = view {
        id = "searchIndexManagementRootView"

        style {
            matchParentDimensions
        }
        scrollable {
            style {
                matchParentDimensions
            }

            verticalLayout {
                style {
                    width = matchParent
                    height = wrapContent
                    padding = 32.px
                }

                horizontalLayout {
                    style {
                        width = matchParent
                    }
                    textView {
                        text = "Address Search Index".localized()
                        style {
                            width = wrapContent
                            fontSize = 20.px
                            fontWeight = "bold"
                        }
                    }
                    horizontalFiller()
                }
                verticalFiller(20)

                horizontalLayout {
                    style {
                        width = 60.percent
                    }
                    horizontalFiller(20)
                    verticalLayout {
                        matchParentWidth()
                        withLoadingAndError(viewModel.getStatusUiState, onRetryClicked = {
                            viewModel.getStatus()
                        }, onLoaded = {
                            clearAllChildren()
                            statusSummary(viewModel.status)
                            onSettingsLoaded()
                        })
                    }
                    horizontalFiller()

                    verticalLayout {
                        style { width = wrapContent }
                        refreshButton()
                        verticalFiller(8)
                        reindexButton()
                    }
                }

                verticalFiller(24)
                favoritePointsIndexSection()

                verticalFiller(24)
                settingsSection()

                verticalFiller(32)
                searchSection()
            }
        }
    }

    private fun LinearLayout.settingsSection() = verticalLayout {
        style {
            width = 60.percent
            height = wrapContent
        }
        settingsContainer = this
    }

    private fun onSettingsLoaded() {
        settingsContainer?.clearAllChildren()
        settingsContainer?.apply { settingsForm(viewModel.status) }
    }

    private fun LinearLayout.settingsForm(status: GetPointsOfInterestSearchStatusEndpoint.Response?) {
        viewModel.setUpdateSettingsUiStateToNull()

        theme.subTitleFit(this, "Elasticsearch settings".localized())

        settingsIsActiveCheckBox = customCheckBox(
            "Use Elasticsearch for address search".localized(),
            isSelected = status?.isActive ?: false,
            id = "elasticsearchIsActiveCheckBox",
        )
        verticalFiller(8)

        settingsUrlInput = theme.labeledTextInput(this, "Elasticsearch server url".localized(), true) {
            text = status?.url ?: ""
            placeholder = "eg. http://localhost:9200"
        }

        verticalFiller(8)
        val errorText = textView {
            style {
                width = wrapContent
                fontSize = 14.px
                color = AppColors.redLight
            }
            isVisible = false
            text = "Please enter a valid server url".localized()
        }
        verticalFiller(8)

        theme.mainButton(this) {
            id = "saveElasticsearchSettings"
            text = "Save settings".localized()
            onClick = {
                onSaveSettingsClicked(errorText)
            }
            withLoadingAndError(viewModel.updateSettingsUiState, onRetryClicked = {
                onSaveSettingsClicked(errorText)
            }, onLoaded = {
                viewModel.getStatus()
            })
        }
    }

    private fun onSaveSettingsClicked(errorText: TextView) {
        val urlInput = settingsUrlInput ?: return
        val url = urlInput.text.trim()
        if (url.isEmpty()) {
            errorText.isVisible = true
            return
        }
        errorText.isVisible = false
        viewModel.updateSettings(settingsIsActiveCheckBox?.isSelected ?: false, url)
    }

    private fun LinearLayout.searchSection() = verticalLayout {
        style {
            width = matchParent
            height = wrapContent
        }

        horizontalLayout {
            style {
                width = matchParent
                alignItems = Alignment.Center
            }
            textView {
                text = "Search points of interest".localized()
                style {
                    width = wrapContent
                    fontSize = 16.px
                    fontWeight = "bold"
                }
            }
            horizontalFiller()
            customCheckBox(
                "Search addresses".localized(),
                isSelected = viewModel.includeAddress,
                onSelection = { viewModel.setIncludeAddress(it) },
            )
            horizontalFiller(16)
            setupDropDownList<AreaDto>(
                "Area".localized(),
                getList = { pageNo, pageSize, searchTerm ->
                    GetAreasEndpoint.remoteProcess(GetAreasEndpoint.Request(pageNo, pageSize, searchTerm)).data.areas
                },
                itemToString = { it.name },
                onItemSelected = { viewModel.setAreaFilter(it) },
                showAutoComplete = true,
                viewWidth = 200.px,
            )
            horizontalFiller(16)
            setupDropDownList<PriceZoneDto>(
                "Price zone".localized(),
                getList = { pageNo, pageSize, searchTerm ->
                    GetPriceZonesEndpoint.remoteProcess(GetPriceZonesEndpoint.Request(pageNo, pageSize, searchTerm)).data.list
                },
                itemToString = { it.name },
                onItemSelected = { viewModel.setPriceZoneFilter(it) },
                showAutoComplete = true,
                viewWidth = 200.px,
            )
            horizontalFiller(16)
            searchTextInput("Search".localized(), viewWidth = 260) {
                viewModel.searchFor(it)
            }
        }

        verticalLayout {
            matchParentDimensions()
            withLoadingAndError(viewModel.searchUiState,
                onRetryClicked = { viewModel.searchPoints() },
                onLoaded = { onPointsLoaded() })

            pointsTableBody = listTable {
                headerCell("Name".localized(), 2)
                headerCell("Name (FR)".localized(), 2)
                headerCell("Address".localized(), 3)
                headerCell("Coordinates".localized(), 2)
            }
            paginationControls = setupPaginationControls(viewModel::getNextPage, viewModel::getPreviousPage)
        }
    }

    private fun onPointsLoaded() {
        paginationControls?.updateWithoutCount(viewModel.pageNo, viewModel.pageSize, viewModel.isLastPageEmpty)
        pointsTableBody?.clearAllChildren()
        pointsTableBody?.apply {
            if (viewModel.points.isEmpty()) {
                tableRow(isClickable = false) {
                    tableCell("No matching points".localized(), 1, 14.px)
                }
                return@apply
            }
            viewModel.points.forEachIndexed { index, point ->
                tableRow(isClickable = false) {
                    tableCell(point.name ?: "-", 2, 14.px)
                    tableCell(point.nameFr ?: "-", 2, 14.px)
                    tableCell(point.address ?: "-", 3, 14.px)
                    tableCell("${point.lat}, ${point.lng}", 2, 14.px)
                }
                if (index != viewModel.points.lastIndex) {
                    verticalSeparator()
                }
            }
        }
    }

    private fun LinearLayout.statusSummary(status: GetPointsOfInterestSearchStatusEndpoint.Response?) {
        if (status == null) {
            theme.text(this) { text = "-" }
            return
        }

        theme.label(this, "Search engine".localized(), false)
        statusLine(
            text = if (status.isActive) "Elasticsearch".localized() else "MySQL (Elasticsearch disabled)".localized(),
            color = if (status.isActive) AppColors.greenDark else AppColors.textLight,
        )

        theme.label(this, "Server".localized(), false)
        theme.text(this) { text = status.url }

        if (status.isActive) {
            theme.label(this, "Connection".localized(), false)
            statusLine(
                text = if (status.isReachable) "Reachable".localized() else "Not reachable".localized(),
                color = if (status.isReachable) AppColors.greenDark else AppColors.redLight,
            )

            theme.label(this, "Cluster status".localized(), false)
            statusLine(
                text = status.clusterStatus ?: "-",
                color = when (status.clusterStatus) {
                    "green" -> AppColors.greenDark
                    "yellow" -> AppColors.warning
                    "red" -> AppColors.redLight
                    else -> AppColors.textLight
                },
            )

            theme.label(this, "Index".localized(), false)
            statusLine(
                text = if (status.indexExists) "Created".localized() else "Missing".localized(),
                color = if (status.indexExists) AppColors.greenDark else AppColors.redLight,
            )

            theme.label(this, "Indexed points".localized(), false)
            statusLine(
                text = "${status.indexedPointsCount} / ${status.databasePointsCount}",
                color = if (status.indexedPointsCount == status.databasePointsCount) AppColors.greenDark
                else AppColors.warning,
            )

            status.error?.let { error ->
                theme.label(this, "Error".localized(), false)
                statusLine(text = error, color = AppColors.redLight)
            }
        } else {
            theme.label(this, "Points in database".localized(), false)
            theme.text(this) { text = status.databasePointsCount.toString() }
        }

        viewModel.lastIndexedPointsCount?.let { count ->
            verticalFiller(12)
            statusLine(text = "Last rebuild indexed $count points".localized(), color = AppColors.greenDark)
        }
    }

    private fun LinearLayout.favoritePointsIndexSection() = verticalLayout {
        matchParentWidth()
        theme.subTitleFit(this, "Saved addresses index".localized())
        verticalFiller(8)
        horizontalLayout {
            style { width = 60.percent }
            horizontalFiller(20)
            verticalLayout {
                matchParentWidth()
                withLoadingAndError(viewModel.getFavoritePointsStatusUiState, onRetryClicked = {
                    viewModel.getFavoritePointsStatus()
                }, onLoaded = {
                    clearAllChildren()
                    favoritePointsStatusSummary(viewModel.favoritePointsStatus)
                })
            }
            horizontalFiller()
            verticalLayout {
                style { width = wrapContent }
                reindexFavoritePointsButton()
            }
        }
    }

    private fun LinearLayout.favoritePointsStatusSummary(
        status: GetFavoritePointsSearchStatusEndpoint.Response?
    ) {
        if (status == null) {
            theme.text(this) { text = "-" }
            return
        }

        if (status.isActive.not()) {
            theme.label(this, "Saved addresses in database".localized(), false)
            theme.text(this) { text = status.databaseFavoritePointsCount.toString() }
            return
        }

        theme.label(this, "Index".localized(), false)
        statusLine(
            text = if (status.indexExists) "Created".localized() else "Missing".localized(),
            color = if (status.indexExists) AppColors.greenDark else AppColors.redLight,
        )

        theme.label(this, "Indexed saved addresses".localized(), false)
        statusLine(
            text = "${status.indexedFavoritePointsCount} / ${status.databaseFavoritePointsCount}",
            color = if (status.indexedFavoritePointsCount == status.databaseFavoritePointsCount) AppColors.greenDark
            else AppColors.warning,
        )

        status.error?.let { error ->
            theme.label(this, "Error".localized(), false)
            statusLine(text = error, color = AppColors.redLight)
        }

        viewModel.lastIndexedFavoritePointsCount?.let { count ->
            verticalFiller(12)
            statusLine(text = "Last rebuild indexed $count saved addresses".localized(), color = AppColors.greenDark)
        }
    }

    private fun LinearLayout.reindexFavoritePointsButton() {
        theme.mainButton(this) {
            id = "reindexFavoritePointsSearchIndex"
            text = "Rebuild index".localized()
            onClick = {
                viewModel.setReindexFavoritePointsUiStateToNull()
                showConfirmationDialogWithRemoteAction(
                    title = "Rebuild saved addresses index".localized(),
                    message = ("This deletes and rebuilds the whole saved addresses index. " +
                            "Saved address search falls back to MySQL while it runs.").localized(),
                    actionButtonText = "Rebuild".localized(),
                    dismissButtonText = null,
                    actionButtonStyle = null,
                    uiState = viewModel.reindexFavoritePointsUiState,
                    action = { viewModel.reindexFavoritePoints() },
                    onConfirmed = { viewModel.getFavoritePointsStatus() },
                )
            }
        }
    }

    private fun LinearLayout.statusLine(text: String, color: Color) = textView {
        this.text = text
        style {
            width = wrapContent
            fontSize = 14.px
            fontWeight = "bold"
            this.color = color
        }
    }

    private fun LinearLayout.refreshButton() {
        theme.secondaryButton(this) {
            id = "refreshSearchIndexStatus"
            text = "Refresh".localized()
            onClick = {
                viewModel.getStatus()
            }
        }
    }

    private fun LinearLayout.reindexButton() {
        theme.mainButton(this) {
            id = "reindexSearchIndex"
            text = "Rebuild index".localized()
            onClick = {
                viewModel.setReindexUiStateToNull()
                showConfirmationDialogWithRemoteAction(
                    title = "Rebuild index".localized(),
                    message = ("This deletes and rebuilds the whole points of interest index. " +
                            "Address search falls back to MySQL while it runs.").localized(),
                    actionButtonText = "Rebuild".localized(),
                    dismissButtonText = null,
                    actionButtonStyle = null,
                    uiState = viewModel.reindexUiState,
                    action = { viewModel.reindex() },
                    onConfirmed = { viewModel.getStatus() },
                )
            }
        }
    }
}
